import React, { ReactNode } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import { Grid } from "@mui/material";

import {
  useGetOrg,
  useGetOrgId,
} from "../../api/organizations/get-organization";
import { usePatchOrganization } from "../../api/organizations/patch-organization";
import { BlocksIcon } from "../../images/Icons/BlocksIcon";
import { CodeWindowIcon } from "../../images/Icons/CodeWindowIcon";
import { isDialogOpen, setDialog } from "../../store/slices/global";
import { intelligemsFullThemeSnippet } from "../../utils/intelligemsThemeSnippets";
import { igDirectScriptAdd } from "../../utils/linkSheet";
import openNewTab from "../../utils/openNewTab";

interface PanelProps {
  icon: ReactNode;
  title: string;
  subtitle: ReactNode;
  buttonText: string;
  onClick: () => Promise<void>;
}

const Panel = ({ title, subtitle, buttonText, onClick, icon }: PanelProps) => {
  return (
    <Grid
      container
      item
      justifyContent={"space-between"}
      sx={{
        flexWrap: "nowrap",
        border: `solid 1px #ECECEC`,
        padding: "16px",
        borderRadius: "8px",
      }}
    >
      {icon}
      <Grid container item direction={"column"} sx={{ paddingLeft: "5px" }}>
        <Grid item textAlign={"left"}>
          <IgTypography>
            <b>{title}</b>
          </IgTypography>
        </Grid>
        <Grid item textAlign={"left"} sx={{ paddingTop: "2px" }}>
          <IgTypography>{subtitle}</IgTypography>
        </Grid>
      </Grid>
      <Grid
        container
        item
        alignItems={"center"}
        textAlign={"end"}
        justifyContent={"end"}
        sx={{ width: "120px" }}
      >
        <IgButton
          sx={{ minWidth: "120px" }}
          onClick={onClick}
          variant={"outlined"}
        >
          {buttonText}
        </IgButton>
      </Grid>
    </Grid>
  );
};

export const ScriptTagDialog = () => {
  const isOpen = useSelector(isDialogOpen("scriptTag"));
  const dispatch = useDispatch();
  const patchOrganization = usePatchOrganization();
  const org = useGetOrg();
  const organizationId = useGetOrgId();

  return (
    <IgDialog
      title={"Add Intelligems To Your Site"}
      handleClose={() => {
        dispatch(setDialog({ dialog: "scriptTag", open: false }));
      }}
      maxWidth={"lg"}
      showDialog={isOpen}
      showCloseIcon={true}
      loading={patchOrganization.isPending}
      dialogHeight={"360px"}
      dialogContentWidth={"600px"}
      hideActions={true}
      rawContent={
        <Grid
          container
          direction={"column"}
          rowSpacing={2}
          sx={{ marginTop: "10px" }}
        >
          <Panel
            icon={
              <Grid
                item
                sx={{
                  width: "60px",
                  height: "50px",
                  paddingRight: "10px",
                }}
              >
                <CodeWindowIcon sx={{ fontSize: "50px" }} />
              </Grid>
            }
            title={"Add Intelligems Script Directly"}
            subtitle={
              <IgTypography>
                {"(Recommended)"}{" "}
                <IgTypography
                  onClick={() => {
                    openNewTab(igDirectScriptAdd, "Add Script to Theme");
                  }}
                  igStyle={"link"}
                  sx={{ fontWeight: 500 }}
                >
                  Learn more here
                </IgTypography>
              </IgTypography>
            }
            buttonText={"Copy Script"}
            onClick={async () => {
              await navigator.clipboard.writeText(
                `${intelligemsFullThemeSnippet}
                <script src="https://cdn.intelligems.io/${organizationId}.js"></script>`
              );
              toast.success("Script Copied!");
            }}
          />
          <Grid item sx={{ marginBottom: "10px" }}>
            <IgTypography>OR</IgTypography>
          </Grid>
          <Panel
            icon={
              <Grid
                item
                sx={{
                  width: "60px",
                  height: "50px",
                  marginTop: "auto",
                  marginBottom: "auto",
                  paddingRight: "10px",
                }}
              >
                <BlocksIcon sx={{ fontSize: "50px" }} />
              </Grid>
            }
            title={"Enable App Embeds"}
            subtitle={"(Slower, not recommended)"}
            buttonText={"Enable"}
            onClick={async () => {
              openNewTab(
                `${
                  org.data.org.shopId
                }/admin/themes/current/editor?context=apps&activateAppId=${
                  import.meta.env.VITE_SHOPIFY_THEME_BLOCKS_UUID
                }/intelligems-script`,
                undefined,
                true
              );
            }}
          />
        </Grid>
      }
    />
  );
};
